import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

export type MomentCategory =
  | "lifecycle"
  | "flow"
  | "capture"
  | "knowledge"
  | "work"
  | "views"
  | "pims"
  | "signal";

export type ActorKind = "user" | "agent" | "device" | "system";

export interface ReplayMoment {
  at: string;
  kind: string;
  category: MomentCategory;
  title: string;
  detail: string | null;
  actorKind: ActorKind;
  actorName: string | null;
  entityKind: string | null;
  entityUuid: string | null;
  stageUuid: string | null;
}

export interface ReplayStage {
  uuid: string;
  name: string;
  position: number;
  status: string;
  captureExpected: boolean;
  enteredAt: string | null;
  completedAt: string | null;
}

export interface ReplayStay {
  roomUuid: string;
  roomName: string;
  enteredAt: string;
  leftAt: string | null;
}

export interface ReplaySegment {
  roomUuid: string;
  start: string;
  end: string;
}

export interface ReplayTurn {
  transcriptUuid: string;
  at: string;
  content: string;
}

export interface ReplayVisit {
  uuid: string;
  patientName: string | null;
  clientName: string | null;
  visitType: string;
  reason: string | null;
  assignedUserName: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

export interface VisitReplay {
  /** When the server's read began — the payload is "as of" this instant. */
  asOf: string;
  visit: ReplayVisit;
  windowStart: string;
  windowEnd: string;
  stages: ReplayStage[];
  stays: ReplayStay[];
  capture: ReplaySegment[];
  turns: ReplayTurn[];
  /** The turn track hit its server cap — the end of the transcript is missing. */
  turnsTruncated: boolean;
  /** False when a concurrent visit shared a room in this window — some
   * room-derived transcripts may belong to the other patient. */
  transcriptAttributionComplete: boolean;
  /** PIMS activity settled before this instant may be retention-pruned.
   * Null when the backend predates BE-2111 and doesn't report it. */
  pimsHistoryAvailableSince: string | null;
  /** A never-closed room stay's transcript sweep was cut off at
   * `open_stay_hours` after entry — extend via the query param. */
  openStayCapped: boolean;
  moments: ReplayMoment[];
}

export const DEFAULT_OPEN_STAY_HOURS = 24;
export const MAX_OPEN_STAY_HOURS = 168;

type RawReplay = components["schemas"]["VisitReplayOut"];

const toReplay = (raw: RawReplay): VisitReplay => ({
  asOf: raw.as_of,
  visit: {
    uuid: raw.visit.uuid,
    patientName: raw.visit.patient_name,
    clientName: raw.visit.client_name,
    visitType: raw.visit.visit_type,
    reason: raw.visit.reason,
    assignedUserName: raw.visit.assigned_user_name,
    scheduledStart: raw.visit.scheduled_start,
    scheduledEnd: raw.visit.scheduled_end,
    cancelledAt: raw.visit.cancelled_at,
    createdAt: raw.visit.created_at,
  },
  windowStart: raw.window_start,
  windowEnd: raw.window_end,
  stages: raw.stages.map((s) => ({
    uuid: s.uuid,
    name: s.name,
    position: s.position,
    status: s.status,
    captureExpected: s.capture_expected,
    enteredAt: s.entered_at,
    completedAt: s.completed_at,
  })),
  stays: raw.stays.map((s) => ({
    roomUuid: s.room_uuid,
    roomName: s.room_name,
    enteredAt: s.entered_at,
    leftAt: s.left_at,
  })),
  capture: raw.capture.map((c) => ({
    roomUuid: c.room_uuid,
    start: c.start,
    end: c.end,
  })),
  turns: raw.turns.map((t) => ({
    transcriptUuid: t.transcript_uuid,
    at: t.at,
    content: t.content,
  })),
  turnsTruncated: raw.turns_truncated,
  // The BE-2111 disclosure fields are additive; an older backend omits them
  // at runtime whatever the generated types claim. Absent = "no known gaps".
  transcriptAttributionComplete:
    (raw as Partial<RawReplay>).transcript_attribution_complete ?? true,
  pimsHistoryAvailableSince:
    (raw as Partial<RawReplay>).pims_history_available_since ?? null,
  openStayCapped: (raw as Partial<RawReplay>).open_stay_capped ?? false,
  moments: raw.moments.map((m) => ({
    at: m.at,
    kind: m.kind,
    category: m.category,
    title: m.title,
    detail: m.detail,
    actorKind: m.actor_kind,
    actorName: m.actor_name,
    entityKind: m.entity_kind,
    entityUuid: m.entity_uuid,
    stageUuid: m.stage_uuid,
  })),
});

export const replayClient = {
  get: (
    visitUuid: string,
    openStayHours = DEFAULT_OPEN_STAY_HOURS,
  ): Promise<VisitReplay> =>
    request(() =>
      apiClient.GET("/visits/{visit_uuid}/replay", {
        params: {
          path: { visit_uuid: visitUuid },
          query: { open_stay_hours: openStayHours },
        },
      }),
    ).then(toReplay),
};
